import { formatMoney } from './formatters/money';

export const NET_WORTH_BUCKET_LABELS = {
  cash: 'Cash',
  investments: 'Investments',
  property: 'Property',
  otherAssets: 'Other Assets',
  debt: 'Liabilities',
};

export const MANUAL_ITEM_TYPE_LABELS = {
  property: 'Property',
  vehicle: 'Vehicle',
  other_asset: 'Other',
  liability: 'Liability',
};

export const OTHER_ASSET_DISPLAY_TYPE_LABELS = {
  vehicle: 'Vehicle',
  collectibles: 'Collectibles',
  jewelry: 'Jewelry',
  cash: 'Cash',
  other: 'Other',
};

export const LIABILITY_DISPLAY_TYPE_LABELS = {
  student_loan: 'Student Loan',
  personal_loan: 'Personal Loan',
  medical_debt: 'Medical Debt',
  tax_debt: 'Tax Debt',
  family_private_loan: 'Family / Private Loan',
  other: 'Other',
};

const OTHER_ASSET_DISPLAY_TYPES = new Set(Object.keys(OTHER_ASSET_DISPLAY_TYPE_LABELS));
const LIABILITY_DISPLAY_TYPES = new Set(Object.keys(LIABILITY_DISPLAY_TYPE_LABELS));
const OTHER_ASSET_NOTE_PREFIX = '[vylo-asset-type:';

export const MANUAL_ITEM_TYPE_OPTIONS = [
  { value: 'property', label: 'Property' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'other_asset', label: 'Other' },
  { value: 'liability', label: 'Liability' },
];

export const OTHER_ASSET_DISPLAY_TYPE_OPTIONS = [
  { value: '', label: 'Select Type...' },
  { value: 'vehicle', label: 'Vehicle' },
  { value: 'collectibles', label: 'Collectibles' },
  { value: 'jewelry', label: 'Jewelry' },
  { value: 'cash', label: 'Cash' },
  { value: 'other', label: 'Other' },
];

export const LIABILITY_DISPLAY_TYPE_OPTIONS = [
  { value: '', label: 'Select Type...' },
  { value: 'student_loan', label: 'Student Loan' },
  { value: 'personal_loan', label: 'Personal Loan' },
  { value: 'medical_debt', label: 'Medical Debt' },
  { value: 'tax_debt', label: 'Tax Debt' },
  { value: 'family_private_loan', label: 'Family / Private Loan' },
  { value: 'other', label: 'Other' },
];

const netWorthCurrencyFormatters = new Map();
const netWorthAxisCurrencyFormatters = new Map();
const netWorthDetailAxisCurrencyFormatters = new Map();
const netWorthWholeCurrencyFormatters = new Map();

const normalizeCurrencyCode = (value) => {
  const currencyCode = String(value || '').trim().toUpperCase();
  return /^[A-Z]{3}$/.test(currencyCode) ? currencyCode : 'USD';
};

const getNetWorthCurrencyFormatter = (currencyCode, compact = false) => {
  const normalized = normalizeCurrencyCode(currencyCode);
  const formatters = compact ? netWorthAxisCurrencyFormatters : netWorthCurrencyFormatters;
  if (!formatters.has(normalized)) {
    formatters.set(normalized, new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalized,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: compact ? 0 : 2,
      maximumFractionDigits: compact ? 0 : 2,
      ...(compact ? { notation: 'compact' } : {}),
    }));
  }
  return formatters.get(normalized);
};

export const formatNetWorthCurrency = (value, currencyCode = 'USD') => {
  const numeric = Number(value) || 0;
  return getNetWorthCurrencyFormatter(currencyCode).format(Math.abs(numeric));
};

export const formatSignedNetWorthCurrency = (value, currencyCode = 'USD') => (
  getNetWorthCurrencyFormatter(currencyCode).format(Number(value) || 0)
);

export const formatNetWorthAxisCurrency = (value, currencyCode = 'USD') => (
  getNetWorthCurrencyFormatter(currencyCode, true).format(Number(value) || 0)
);

export const formatNetWorthDetailAxisCurrency = (value, currencyCode = 'USD') => {
  const normalized = normalizeCurrencyCode(currencyCode);
  if (!netWorthDetailAxisCurrencyFormatters.has(normalized)) {
    netWorthDetailAxisCurrencyFormatters.set(normalized, new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalized,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
      notation: 'compact',
    }));
  }
  return netWorthDetailAxisCurrencyFormatters.get(normalized).format(Number(value) || 0);
};

export const formatNetWorthDelta = (value, currencyCode = 'USD') => {
  const numeric = Number(value) || 0;
  const prefix = numeric < 0 ? '-' : '';
  return `${prefix}${formatNetWorthCurrency(numeric, currencyCode)}`;
};

const toFiniteComparisonValue = (value) => {
  if (value == null || typeof value === 'boolean') return null;
  if (typeof value === 'string' && value.trim() === '') return null;
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

const getWholeCurrencyFormatter = (currencyCode) => {
  const normalized = normalizeCurrencyCode(currencyCode);
  if (!netWorthWholeCurrencyFormatters.has(normalized)) {
    netWorthWholeCurrencyFormatters.set(normalized, new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalized,
      currencyDisplay: 'narrowSymbol',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }));
  }
  return netWorthWholeCurrencyFormatters.get(normalized);
};

export const getNetWorthObservationDateKey = (point) => {
  const rawDate = point?.snapshotDate || point?.date || point?.key;
  return String(rawDate || '').match(/^(\d{4}-\d{2}-\d{2})/)?.[1] || '';
};

export const formatNetWorthComparisonDate = (point) => {
  const rawDate = point?.snapshotDate || point?.date || point?.key;
  const match = String(rawDate || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsed = match
    ? new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
    : new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' });
};

export const formatNetWorthComparisonChange = ({ change, basis, point, currencyCode = 'USD' }) => {
  const numericChange = toFiniteComparisonValue(change);
  if (numericChange == null) return '';
  const numericBasis = toFiniteComparisonValue(basis);
  const sign = numericChange > 0 ? '+' : numericChange < 0 ? '-' : '';
  const amount = `${sign}${getWholeCurrencyFormatter(currencyCode).format(Math.abs(numericChange))}`;
  const percentage = numericBasis != null && Math.abs(numericBasis) > 0
    ? (numericChange / Math.abs(numericBasis)) * 100
    : null;
  const percentageSign = percentage > 0 ? '+' : percentage < 0 ? '-' : '';
  const dateLabel = formatNetWorthComparisonDate(point);
  return [
    amount,
    percentage == null ? '' : `(${percentageSign}${Math.abs(percentage).toFixed(1)}%)`,
    dateLabel ? `since ${dateLabel}` : '',
  ].filter(Boolean).join(' ');
};

export const formatNetWorthKpi = (value) => {
  const numeric = Number(value) || 0;
  return formatMoney(numeric, { context: 'kpi' });
};

export const formatNetWorthKpiDelta = (value) => {
  const numeric = Number(value) || 0;
  const prefix = numeric < 0 ? '-' : '';
  return `${prefix}${formatNetWorthKpi(Math.abs(numeric))}`;
};

export const formatNetWorthMonthLabel = (date = new Date()) =>
  new Date(date).toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const parseDisplayDate = (value) => {
  const raw = String(value || '');
  const dateOnlyMatch = raw.match(DATE_ONLY_PATTERN);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(Number(year), Number(month) - 1, Number(day));
  }
  return new Date(value);
};

// A sync attempt / snapshot date is not evidence of a balance update.
export const latestNetWorthUpdate = (summary) => {
  const values = [
    ...(summary?.accounts || []).filter((account) => !account.isIgnored)
      .map((account) => account.balanceFreshness?.updatedAt || account.balanceUpdatedAt),
    ...(summary?.manualItems || []).map((item) => item.lastReviewedAt || item.updatedAt),
  ].filter(Boolean).map((value) => new Date(value).getTime()).filter(Number.isFinite);
  return values.length ? new Date(Math.max(...values)).toISOString() : null;
};

export const formatBalanceUpdatedAt = (value) => {
  if (!value) return 'Never';
  const date = parseDisplayDate(value);
  if (Number.isNaN(date.getTime())) return 'Never';
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

export const bucketManualItemForDisplay = (itemType) => {
  if (itemType === 'property') return 'property';
  if (itemType === 'liability') return 'debt';
  return 'otherAssets';
};

export const stripOtherAssetDisplayTypeMetadata = (notes = '') => {
  const raw = String(notes || '');
  if (!raw.startsWith(OTHER_ASSET_NOTE_PREFIX)) return raw;
  const end = raw.indexOf(']');
  if (end === -1) return raw;
  return raw.slice(end + 1).replace(/^\s+/, '');
};

export const getOtherAssetDisplayType = (item = {}) => {
  if (item?.assetSubtype && OTHER_ASSET_DISPLAY_TYPES.has(item.assetSubtype)) {
    return item.assetSubtype;
  }
  if (item?.itemType === 'vehicle') return 'vehicle';
  if (item?.itemType !== 'other_asset') return item?.itemType || 'other';
  const raw = String(item?.notes || '');
  if (raw.startsWith(OTHER_ASSET_NOTE_PREFIX)) {
    const end = raw.indexOf(']');
    const displayType = raw.slice(OTHER_ASSET_NOTE_PREFIX.length, end);
    if (OTHER_ASSET_DISPLAY_TYPES.has(displayType)) return displayType;
  }
  return 'other';
};

export const getLiabilityDisplayType = (item = {}) => {
  if (item?.liabilitySubtype && LIABILITY_DISPLAY_TYPES.has(item.liabilitySubtype)) {
    return item.liabilitySubtype;
  }
  if (item?.itemType !== 'liability') return item?.itemType || 'other';
  return 'other';
};

export const serializeOtherAssetNotes = (displayType, notes = '') => {
  const cleanNotes = stripOtherAssetDisplayTypeMetadata(notes).trim();
  if (!OTHER_ASSET_DISPLAY_TYPES.has(displayType) || displayType === 'vehicle') {
    return cleanNotes;
  }
  return cleanNotes
    ? `${OTHER_ASSET_NOTE_PREFIX}${displayType}] ${cleanNotes}`
    : `${OTHER_ASSET_NOTE_PREFIX}${displayType}]`;
};

export const getPersistedManualItemType = (displayType) => {
  if (displayType === 'vehicle') return 'vehicle';
  if (OTHER_ASSET_DISPLAY_TYPES.has(displayType)) return 'other_asset';
  if (LIABILITY_DISPLAY_TYPES.has(displayType)) return 'liability';
  return displayType;
};

export const sumClassifiedAccounts = (accounts = []) => accounts.reduce((acc, account) => {
  const category = account?.netWorthCategory || account?.category;
  const rawBalance = Number(account?.currentBalance) || 0;
  const netWorthAmount = Number.isFinite(Number(account?.netWorthAmount))
    ? Number(account.netWorthAmount)
    : category === 'debt'
      ? Math.abs(rawBalance)
      : rawBalance;
  if (category === 'cash') acc.cash += netWorthAmount;
  if (category === 'investments') acc.investments += netWorthAmount;
  if (category === 'debt') acc.debt += netWorthAmount;
  return acc;
}, {
  cash: 0,
  investments: 0,
  property: 0,
  otherAssets: 0,
  debt: 0,
});

export const sumManualItems = (items = []) => items.reduce((acc, item) => {
  const value = Number(item?.estimatedValue) || 0;
  const bucket = bucketManualItemForDisplay(item?.itemType);
  if (bucket === 'property') acc.property += value;
  if (bucket === 'otherAssets') acc.otherAssets += value;
  if (bucket === 'debt') acc.debt += value;
  return acc;
}, {
  cash: 0,
  investments: 0,
  property: 0,
  otherAssets: 0,
  debt: 0,
});

export const combineBreakdown = (accountsBreakdown, manualBreakdown) => ({
  cash: (accountsBreakdown.cash || 0) + (manualBreakdown.cash || 0),
  investments: (accountsBreakdown.investments || 0) + (manualBreakdown.investments || 0),
  property: (accountsBreakdown.property || 0) + (manualBreakdown.property || 0),
  otherAssets: (accountsBreakdown.otherAssets || 0) + (manualBreakdown.otherAssets || 0),
  debt: (accountsBreakdown.debt || 0) + (manualBreakdown.debt || 0),
});

export const buildNetWorthTotals = (breakdown) => {
  const totalAssets =
    (Number(breakdown.cash) || 0)
    + (Number(breakdown.investments) || 0)
    + (Number(breakdown.property) || 0)
    + (Number(breakdown.otherAssets) || 0);
  const totalLiabilities = Number(breakdown.debt) || 0;
  return {
    totalAssets,
    totalLiabilities,
    netWorth: totalAssets - totalLiabilities,
  };
};

export const monthTitle = (value) => {
  const date = value ? new Date(value) : new Date();
  return `${formatNetWorthMonthLabel(date).replace(/\s+\d{4}$/, '')} Net Worth`;
};
